import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { authApi } from '@/services/api'
import { useAuthStore } from '@/store/auth.store'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import ReCaptcha from '@/components/ui/ReCaptcha'
import OTPInput from '@/components/ui/OTPInput'
import AuthFooter from '@/components/layout/AuthFooter'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [showOtpStep, setShowOtpStep] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (showOtpStep && timeRemaining !== null) {
      if (timeRemaining <= 0) {
        setError('Your verification code has expired. Please restart the login process.')
        setShowOtpStep(false)
        setTimeRemaining(null)
        return
      }

      const timer = setInterval(() => {
        setTimeRemaining((prev) => (prev !== null && prev > 0 ? prev - 1 : 0))
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [showOtpStep, timeRemaining])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Step 1: Submit email and password
      const response = await authApi.login(email, password, recaptchaToken)

      if (response.success && response.data) {
        // Check if OTP is required
        if (response.data.otp_required) {
          setUserId(response.data.user_id || null)
          setTimeRemaining((response.data.expires_in_minutes || 7) * 60)
          setShowOtpStep(true)
        } else {
          // Old flow: direct login (shouldn't happen with new implementation)
          setAuth(response.data.user, response.data.token)
          navigate('/dashboard')
        }
      }
    } catch (err: any) {
      // Check if it's a maintenance mode error
      if (err.response?.status === 503) {
        const maintenanceMsg = err.response?.data?.errors?.maintenance_message || err.response?.data?.message
        const endTime = err.response?.data?.errors?.end_datetime

        let displayMsg = maintenanceMsg || 'System is currently under maintenance'
        if (endTime) {
          displayMsg += `\n\nExpected to resume: ${new Date(endTime).toLocaleString()}`
        }
        setError(displayMsg)
      } else {
        setError(err.response?.data?.message || err.message || 'Login failed. Please check your credentials.')
      }
      // Reset reCAPTCHA on error
      setRecaptchaToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (otp.length !== 7) {
      setError('Please enter the complete 7-digit verification code.')
      return
    }

    setIsLoading(true)

    try {
      // Step 2: Verify OTP
      const response = await authApi.verifyOtp(userId!, otp)

      if (response.success && response.data) {
        setAuth(response.data.user, response.data.token)
        navigate('/dashboard')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Invalid verification code. Please try again.')
      setOtp('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowOtpStep(false)
    setOtp('')
    setUserId(null)
    setTimeRemaining(null)
    setError('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <Link to="/">
              <h1 className="text-4xl font-bold text-primary-600 mb-2 hover:text-primary-700 transition-colors cursor-pointer">BarterSheet</h1>
            </Link>
            <p className="text-gray-600">
              {showOtpStep ? 'Enter Verification Code' : 'Sign in to your account'}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6">
              <Alert type="error" message={error} onClose={() => setError('')} />
            </div>
          )}

          {!showOtpStep ? (
            /* Email/Password Login Form */
            <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* reCAPTCHA */}
            <ReCaptcha
              onChange={(token) => setRecaptchaToken(token)}
              onError={() => setError('reCAPTCHA verification failed. Please try again.')}
            />

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
              >
                Sign In
              </Button>
            </form>
          ) : (
            /* OTP Verification Form */
            <div>
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">
                        We've sent a verification code to your email
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Please check your inbox and enter the 7-digit code below
                      </p>
                    </div>
                  </div>
                </div>

                {timeRemaining !== null && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600">
                      Time remaining:{' '}
                      <span className={`font-bold ${timeRemaining < 60 ? 'text-red-600' : 'text-primary-600'}`}>
                        {formatTime(timeRemaining)}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                    Enter Verification Code
                  </label>
                  <OTPInput
                    value={otp}
                    onChange={setOtp}
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={isLoading}
                  disabled={otp.length !== 7}
                >
                  Verify & Sign In
                </Button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  ← Back to login
                </button>
              </form>
            </div>
          )}

          {!showOtpStep && (
            <>
              {/* Register Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
                    Register now
                  </Link>
                </p>
              </div>

              {/* Demo Credentials */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Only real humans allowed</p>
                <p className="text-xs text-gray-600">Please be aware of fraudulent people. Trust but verify everything.</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <AuthFooter />
      </div>
    </div>
  )
}
